import React, { useState, useEffect } from "react";
import { getMfaStatus, disableMfa } from "../../services/mfaService";
import MfaSetup from "./MfaSetup";

const MfaSettings: React.FC = () => {
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [phoneLastFour, setPhoneLastFour] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);
  const [error, setError] = useState("");

  const fetchStatus = async () => {
    try {
      const status = await getMfaStatus();
      setMfaEnabled(status.mfa_enabled);
      setPhoneLastFour(status.phone_last_four);
    } catch (err) {
      console.error("Failed to fetch MFA status:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleDisable = async () => {
    setIsDisabling(true);
    setError("");

    try {
      await disableMfa();
      setMfaEnabled(false);
      setPhoneLastFour(null);
      setShowDisableConfirm(false);
    } catch (err: any) {
      setError(
        err.response?.data?.error || "Failed to disable MFA. Please try again."
      );
    } finally {
      setIsDisabling(false);
    }
  };

  const handleSetupComplete = () => {
    setShowSetup(false);
    fetchStatus();
  };

  if (isLoading) {
    return (
      <div className="mfa-panel">
        <h3 style={{ margin: "8px 0px" }}>Two-Factor Authentication</h3>
        <p className="mfa-loading-text">Loading...</p>
      </div>
    );
  }

  if (showSetup) {
    return (
      <>
        <div className="mfa-panel">
          <h3 style={{ margin: "8px 0px" }}>Two-Factor Authentication</h3>
        </div>
        <MfaSetup
          onComplete={handleSetupComplete}
          onCancel={() => setShowSetup(false)}
        />
      </>
    );
  }

  return (
    <div className="mfa-panel">
      <h3 style={{ margin: "8px 0px" }}>Two-Factor Authentication</h3>

      {mfaEnabled ? (
        <div className="mfa-panel-body">
          <div className="mfa-badge-row">
            <span className="mfa-badge mfa-badge--enabled">Enabled</span>
          </div>
          <p className="mfa-description">
            Verification codes are sent to ****{phoneLastFour}
          </p>

          {showDisableConfirm ? (
            <div className="mfa-confirm">
              <p className="mfa-confirm-text">
                Are you sure you want to disable two-factor authentication? This
                will make your account less secure.
              </p>
              {error && <p className="mfa-confirm-error">{error}</p>}
              <div className="mfa-confirm-actions">
                <button
                  onClick={handleDisable}
                  disabled={isDisabling}
                  className="mfa-btn mfa-btn--compact mfa-btn--danger"
                >
                  {isDisabling ? "Disabling..." : "Yes, Disable MFA"}
                </button>
                <button
                  onClick={() => {
                    setShowDisableConfirm(false);
                    setError("");
                  }}
                  className="mfa-btn mfa-btn--compact mfa-btn--secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowDisableConfirm(true)}
              className="mfa-btn mfa-btn--compact mfa-btn--secondary"
            >
              Disable Two-Factor Authentication
            </button>
          )}
        </div>
      ) : (
        <div className="mfa-panel-body">
          <div className="mfa-badge-row">
            <span className="mfa-badge mfa-badge--disabled">Not enabled</span>
          </div>
          <p className="mfa-description">
            Add an extra layer of security by requiring a verification code from
            your phone when you log in.
          </p>
          <button
            onClick={() => setShowSetup(true)}
            className="mfa-btn mfa-btn--primary"
          >
            Enable Two-Factor Authentication
          </button>
        </div>
      )}
    </div>
  );
};

export default MfaSettings;
